import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  ShieldCheck, ShieldOff, ChevronLeft, ChevronRight,
  User as UserIcon, CheckCircle, Clock, X, AlertTriangle, RefreshCw,
  Search, Calendar, ChevronDown, Check, Download, FileText, Ban
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../../components/ui/button";
import AdminService from "../../sevices/admin.service";
import { toastSuccess, toastError } from "../../components/common/toastNotification";
import { fixPhotoUrl } from "../../lib/utils";
import SafeImage from "../../components/common/SafeImage";

const formatFullDate = (val) => {
  if (!val) return "—";
  let d;
  if (Array.isArray(val)) {
    const [y, mo, day, h = 0, mi = 0] = val;
    d = new Date(y, mo - 1, day, h, mi);
  } else {
    d = new Date(val);
  }
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
};

const TIME_RANGES = [
  { label: "Lifetime", value: "lifetime" },
  { label: "Today (24h)", value: "today" },
  { label: "Last 7 Days", value: "last7days" },
  { label: "Last 30 Days", value: "last30days" },
  { label: "Last Year", value: "lastyear" },
];

const SORT_OPTIONS = [
  { label: "Newest First", value: "newest" },
  { label: "Oldest First", value: "oldest" },
];

const isDateInRange = (dateVal, rangeValue) => {
  if (!dateVal || rangeValue === "lifetime") return true;
  let d;
  if (Array.isArray(dateVal)) {
    const [y, mo, day, h = 0, mi = 0] = dateVal;
    d = new Date(y, mo - 1, day, h, mi);
  } else {
    d = new Date(dateVal);
  }
  if (isNaN(d.getTime())) return true;

  const now = new Date();
  const diffTime = now.getTime() - d.getTime();
  const diffDays = diffTime / (1000 * 3600 * 24);

  if (rangeValue === "today") return diffDays <= 1;
  if (rangeValue === "last7days") return diffDays <= 7;
  if (rangeValue === "last30days") return diffDays <= 30;
  if (rangeValue === "lastyear") return diffDays <= 365;
  return true;
};

/* ─── Custom Sleek Floating Date Filter Dropdown ────────── */
const DateFilterDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = TIME_RANGES.find((tr) => tr.value === value) || TIME_RANGES[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 px-3.5 py-2.5 bg-white border border-purple-200 text-purple-700 hover:text-purple-900 text-xs font-semibold rounded-xl shadow-2xs hover:border-purple-300 hover:bg-purple-50/50 transition-all cursor-pointer"
      >
        <Calendar className="w-4 h-4 text-purple-600" />
        <span>{selectedOption.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-purple-500 transition-transform duration-200 ${isOpen ? "rotate-180 text-purple-700" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 sm:right-0 mt-1.5 w-48 bg-white rounded-2xl shadow-xl border border-gray-100 py-1.5 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
          <div className="px-3.5 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            TIMEFRAME RANGE
          </div>
          {TIME_RANGES.map((tr) => {
            const isSelected = tr.value === value;
            return (
              <button
                key={tr.value}
                type="button"
                onClick={() => {
                  onChange(tr.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3.5 py-2 text-xs font-semibold flex items-center justify-between transition-colors cursor-pointer ${isSelected
                  ? "bg-purple-50 text-purple-700 font-bold"
                  : "text-gray-700 hover:bg-gray-50"
                  }`}
              >
                <span>{tr.label}</span>
                {isSelected && <Check className="w-3.5 h-3.5 text-purple-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ─── Custom Floating Sort Dropdown ────────── */
const SortDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = SORT_OPTIONS.find((opt) => opt.value === value) || SORT_OPTIONS[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-700 text-xs font-medium rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all cursor-pointer"
      >
        <span className="text-gray-400">Sort by:</span>
        <span className="font-semibold text-gray-800">{selectedOption.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1.5 w-44 bg-white rounded-2xl shadow-xl border border-gray-100 py-1.5 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
          {SORT_OPTIONS.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3.5 py-2 text-xs font-semibold flex items-center justify-between transition-colors cursor-pointer ${isSelected
                  ? "bg-purple-50 text-purple-700 font-bold"
                  : "text-gray-700 hover:bg-gray-50"
                  }`}
              >
                <span>{opt.label}</span>
                {isSelected && <Check className="w-3.5 h-3.5 text-purple-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ─── Lightbox Modal ────────── */
const Lightbox = ({ url, title, onClose }) => {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!url) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" onClick={onClose}>
      <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between w-full mb-3 px-1">
          <p className="text-white text-sm font-semibold">{title || "Document Preview"}</p>
          <button onClick={onClose} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>
        <SafeImage
          src={url}
          alt={title || "Document"}
          className="max-w-full max-h-[80vh] rounded-2xl object-contain shadow-2xl"
          fallback={
            <div className="p-12 text-center text-white/60 bg-white/5 rounded-2xl border border-white/10">
              <FileText className="w-12 h-12 mx-auto mb-2 text-white/40" />
              <p className="text-sm font-medium">Unable to preview document</p>
            </div>
          }
        />
        <a
          href={fixPhotoUrl(url)}
          target="_blank"
          rel="noreferrer"
          download
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-semibold backdrop-blur-sm transition-colors"
        >
          <Download className="w-4 h-4" /> Download Original File
        </a>
      </div>
    </div>
  );
};

/* ─── Reject Modal ────────── */
const RejectModal = ({ user, onConfirm, onCancel, loading }) => {
  const [reason, setReason] = useState("");
  if (!user) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-xs" onClick={onCancel} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-100">
        <div className="h-1.5 w-full bg-rose-500" />
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-2xl bg-rose-50 flex items-center justify-center flex-shrink-0 text-rose-600">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-base leading-tight">Reject Verification</h3>
              <p className="text-xs text-gray-500 mt-0.5">{user.fullName || user.email}</p>
            </div>
          </div>

          <div className="rounded-2xl p-4 text-xs bg-rose-50/60 text-rose-900 border border-rose-100 leading-relaxed">
            Rejecting will reset their submitted documents and request them to re-upload clear photos.
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1.5 block">
              Rejection Reason <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              rows={3}
              className="w-full rounded-2xl border border-gray-200 text-xs p-3 resize-none focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400 bg-gray-50/50"
              placeholder="e.g. ID photo is blurry, selfie face does not match document..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div className="flex gap-2.5 pt-1">
            <button
              type="button"
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 transition-all shadow-md active:scale-95 disabled:opacity-50 cursor-pointer"
              disabled={loading}
              onClick={() => onConfirm(reason)}
            >
              {loading ? "Rejecting..." : "Reject Documents"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Ban Confirmation Modal ────────── */
const BanModal = ({ user, onConfirm, onCancel, loading }) => {
  if (!user) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-xs" onClick={onCancel} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-100">
        <div className="h-1.5 w-full bg-rose-600" />
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-2xl bg-rose-50 flex items-center justify-center flex-shrink-0 text-rose-600">
              <Ban className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-base leading-tight">Ban User Account?</h3>
              <p className="text-xs text-gray-500 mt-0.5">{user.fullName || user.email}</p>
            </div>
          </div>

          <div className="rounded-2xl p-4 text-xs bg-rose-50/70 text-rose-900 border border-rose-100 leading-relaxed">
            Are you sure you want to ban <strong>{user.fullName || user.email}</strong>? This user will be suspended from signing into Signova immediately.
          </div>

          <div className="flex gap-2.5 pt-1">
            <button
              type="button"
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 transition-all shadow-md active:scale-95 disabled:opacity-50 cursor-pointer"
              disabled={loading}
              onClick={() => onConfirm(user.id)}
            >
              {loading ? "Banning..." : "Ban User"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Side Slide-over Review Drawer ────────── */
const VerificationDrawer = ({ user, onClose, onApprove, onReject, onUnverify, actionLoading, onBanUser }) => {
  const navigate = useNavigate();
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [noteText, setNoteText] = useState("");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handler);
    };
  }, [onClose]);

  if (!user) return null;

  const reqIdFormatted = `#VR-${String(user.id).padStart(4, "0")}`;
  const usrIdFormatted = `USR-${String(user.id).padStart(6, "0")}`;

  const isVerified = user.identityVerified;
  const isRejected = user.verificationRejected;

  const statusLabel = isVerified ? "Verified" : isRejected ? "Rejected" : "Pending Review";
  const statusBadgeStyle = isVerified
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : isRejected
      ? "bg-rose-50 text-rose-700 border-rose-200"
      : "bg-amber-50 text-amber-700 border-amber-200";

  const userPhoto = fixPhotoUrl(user.photoUrl1);
  const govIdPhoto = fixPhotoUrl(user.governmentIdUrl);
  const selfiePhoto = fixPhotoUrl(user.selfieUrl);

  const verificationTypeLabel = govIdPhoto && selfiePhoto
    ? "Government ID & Selfie"
    : govIdPhoto
      ? "Government ID"
      : selfiePhoto
        ? "Selfie Verification"
        : "Identity Verification";

  return (
    <>
      <div className="fixed inset-0 z-50 flex justify-end">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-xs transition-opacity" onClick={onClose} />

        {/* Drawer Panel */}
        <div className="relative bg-white w-full max-w-lg h-full shadow-2xl flex flex-col z-10 overflow-hidden border-l border-gray-100 animate-in slide-in-from-right duration-300">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
            <div className="flex items-center gap-2.5">
              <h2 className="text-lg font-bold text-gray-900">Verification Request {reqIdFormatted}</h2>
              <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${statusBadgeStyle}`}>
                {statusLabel}
              </span>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Drawer Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Status Alert Banner */}
            {isRejected && (
              <div className="p-3.5 rounded-2xl bg-rose-50 border border-rose-200/80 flex items-center gap-3 text-xs">
                <div className="w-8 h-8 rounded-xl bg-rose-100 flex items-center justify-center text-rose-600 flex-shrink-0">
                  <X className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-bold text-rose-900">Verification Rejected</p>
                  <p className="text-[11px] text-rose-700 mt-0.5">This applicant's verification documents were rejected.</p>
                </div>
              </div>
            )}

            {isVerified && (
              <div className="p-3.5 rounded-2xl bg-emerald-50 border border-emerald-200/80 flex items-center gap-3 text-xs">
                <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600 flex-shrink-0">
                  <Check className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-bold text-emerald-900">Identity Verified</p>
                  <p className="text-[11px] text-emerald-700 mt-0.5">This user's identity is approved and verified on the platform.</p>
                </div>
              </div>
            )}

            {/* Applicant Section */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Applicant</p>
              <div className="flex items-center justify-between p-4 rounded-2xl bg-gray-50/80 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-purple-100 border border-purple-200 flex-shrink-0">
                    <SafeImage
                      src={userPhoto}
                      alt={user.fullName || "User"}
                      className="w-full h-full object-cover"
                      fallback={
                        <div className="w-full h-full flex items-center justify-center text-purple-600 font-bold text-lg">
                          {user.fullName?.charAt(0) || "U"}
                        </div>
                      }
                    />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm leading-snug">{user.fullName || "Anonymous"}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{user.email}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">Joined {formatFullDate(user.createdAt)}</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/users?userId=${user.id}`, { state: { from: '/verification', user: user, email: user.email, search: user.email } })}
                  className="px-3.5 py-1.5 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-all flex items-center gap-1 shadow-2xs cursor-pointer"
                >
                  View Profile
                </button>
              </div>
            </div>

            {/* Application Metadata Grid */}
            <div className="grid grid-cols-2 gap-4 p-4 rounded-2xl bg-gray-50/50 border border-gray-100 text-xs">
              <div>
                <span className="text-gray-400 block font-medium">Verification Type</span>
                <span className="font-semibold text-purple-700 block mt-0.5">{verificationTypeLabel}</span>
              </div>
              <div>
                <span className="text-gray-400 block font-medium">Submitted On</span>
                <span className="font-semibold text-gray-900 block mt-0.5">{formatFullDate(user.verificationSubmittedAt || user.updatedAt || user.createdAt)}</span>
              </div>
              <div>
                <span className="text-gray-400 block font-medium">Email Address</span>
                <span className="font-semibold text-gray-900 block mt-0.5 truncate">{user.email}</span>
              </div>
              <div>
                <span className="text-gray-400 block font-medium">User ID</span>
                <span className="font-semibold text-gray-900 block mt-0.5 font-mono">{usrIdFormatted}</span>
              </div>
            </div>

            {/* Submitted Documents Section */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Submitted Documents</p>
              <div className="space-y-3">
                {/* Government ID Front */}
                {govIdPhoto && (
                  <div
                    onClick={() => setSelectedDoc({ url: govIdPhoto, title: "Government ID (Front)" })}
                    className="flex items-center justify-between p-3 rounded-2xl bg-white border border-gray-200 hover:border-purple-300 hover:bg-purple-50/20 transition-all cursor-pointer group shadow-2xs"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center text-purple-600 flex-shrink-0">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-xs text-gray-900">Government ID (Front)</p>
                        <p className="text-[11px] text-gray-400">id_front.jpg • Click to preview</p>
                      </div>
                    </div>
                    <div className="w-14 h-10 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0 bg-gray-50">
                      <SafeImage
                        src={govIdPhoto}
                        alt="Gov ID"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        fallback={
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <FileText className="w-4 h-4" />
                          </div>
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Applicant Selfie */}
                {selfiePhoto && (
                  <div
                    onClick={() => setSelectedDoc({ url: selfiePhoto, title: "Applicant Selfie" })}
                    className="flex items-center justify-between p-3 rounded-2xl bg-white border border-gray-200 hover:border-purple-300 hover:bg-purple-50/20 transition-all cursor-pointer group shadow-2xs"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center text-purple-600 flex-shrink-0">
                        <UserIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-xs text-gray-900">Applicant Selfie</p>
                        <p className="text-[11px] text-gray-400">selfie.jpg • Click to preview</p>
                      </div>
                    </div>
                    <div className="w-14 h-10 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0 bg-gray-50">
                      <SafeImage
                        src={selfiePhoto}
                        alt="Selfie"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        fallback={
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <UserIcon className="w-4 h-4" />
                          </div>
                        }
                      />
                    </div>
                  </div>
                )}

                {!govIdPhoto && !selfiePhoto && (
                  <div className="p-6 text-center rounded-2xl border border-dashed border-gray-200 text-gray-400 text-xs">
                    No documents attached for this request.
                  </div>
                )}
              </div>
            </div>

            {/* Notes Section */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Notes</p>
              <textarea
                rows={2}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add note (optional)..."
                className="w-full rounded-2xl border border-gray-200 p-3 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 bg-gray-50/50 resize-none"
              />
            </div>
          </div>

          {/* Drawer Footer Actions */}
          <div className="p-4 border-t border-gray-100 bg-white flex items-center gap-3 sticky bottom-0">
            {isVerified ? (
              <button
                onClick={() => onUnverify(user.id)}
                disabled={!!actionLoading}
                className="flex-1 py-3 px-4 rounded-xl text-xs font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-200 transition-all shadow-xs active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <X className="w-4 h-4 text-gray-500" />
                {actionLoading === `unverify-${user.id}` ? "Revoking..." : "Revoke Verification"}
              </button>
            ) : isRejected ? (
              <button
                onClick={() => onApprove(user.id)}
                disabled={!!actionLoading}
                className="flex-1 py-3 px-4 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-all shadow-md active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
                {actionLoading === `approve-${user.id}` ? "Approving..." : "Re-evaluate & Approve"}
              </button>
            ) : (
              <>
                <button
                  onClick={() => onApprove(user.id)}
                  disabled={!!actionLoading}
                  className="flex-1 py-3 px-4 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-all shadow-md active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  <Check className="w-4 h-4" />
                  {actionLoading === `approve-${user.id}` ? "Approving..." : "Approve"}
                </button>
                <button
                  onClick={() => onReject(user)}
                  disabled={!!actionLoading}
                  className="flex-1 py-3 px-4 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 transition-all shadow-md active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                  {actionLoading === `reject-${user.id}` ? "Rejecting..." : "Reject"}
                </button>
              </>
            )}
            <button
              onClick={() => onBanUser(user)}
              title="Ban user account"
              className="px-3.5 py-3 rounded-xl border border-gray-200 text-gray-600 hover:text-rose-600 hover:border-rose-200 hover:bg-rose-50 transition-all text-xs font-semibold flex items-center gap-1 cursor-pointer"
            >
              <Ban className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {selectedDoc && (
        <Lightbox url={selectedDoc.url} title={selectedDoc.title} onClose={() => setSelectedDoc(null)} />
      )}
    </>
  );
};

/* ─── Main Verification Component ────────── */
const Verification = () => {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters & State
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL, PENDING, VERIFIED, REJECTED
  const [timeRange, setTimeRange] = useState("lifetime");
  const [sortBy, setSortBy] = useState("newest");

  const location = useLocation();
  const [selectedUser, setSelectedUser] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [banTarget, setBanTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (location.state?.returnUser) {
      setSelectedUser(location.state.returnUser);
    }
  }, [location.state]);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    AdminService.getUsers(0, 100, "all")
      .then((res) => {
        const data = res?.data?.data;
        const all = data?.content || [];
        const requests = all.filter(
          (u) => u.selfieUrl || u.governmentIdUrl || u.identityVerified || u.verificationRejected
        );
        setUsers(requests);
      })
      .catch(() => toastError("Failed to load verification requests"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleApprove = async (userId) => {
    setActionLoading(`approve-${userId}`);
    try {
      await AdminService.verifyUser(userId);
      toastSuccess("User identity verified successfully.");
      fetchUsers();
      window.dispatchEvent(new Event("refreshBadgeCounts"));
      setSelectedUser(null);
    } catch {
      toastError("Failed to approve verification");
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnverify = async (userId) => {
    setActionLoading(`unverify-${userId}`);
    try {
      await AdminService.unverifyUser(userId);
      toastSuccess("User verification revoked.");
      fetchUsers();
      window.dispatchEvent(new Event("refreshBadgeCounts"));
      setSelectedUser(null);
    } catch {
      toastError("Failed to revoke verification");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (reason) => {
    if (!rejectTarget) return;
    setActionLoading(`reject-${rejectTarget.id}`);
    try {
      await AdminService.rejectVerification(rejectTarget.id, reason);
      toastSuccess("Verification documents rejected.");
      fetchUsers();
      window.dispatchEvent(new Event("refreshBadgeCounts"));
      setRejectTarget(null);
      setSelectedUser(null);
    } catch {
      toastError("Failed to reject verification");
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmBan = async (userId) => {
    if (!userId) return;
    setActionLoading(`ban-${userId}`);
    try {
      await AdminService.banUser(userId);
      toastSuccess("User has been banned successfully.");
      fetchUsers();
      setBanTarget(null);
      setSelectedUser(null);
    } catch {
      toastError("Failed to ban user");
    } finally {
      setActionLoading(null);
    }
  };

  // Filtered & Sorted verification requests
  const filteredUsers = useMemo(() => {
    let result = users.filter((u) => {
      // Status Filter
      const isVerified = u.identityVerified;
      const isRejected = u.verificationRejected;
      const isPending = !isVerified && !isRejected;

      if (statusFilter === "PENDING" && !isPending) return false;
      if (statusFilter === "VERIFIED" && !isVerified) return false;
      if (statusFilter === "REJECTED" && !isRejected) return false;

      // Search Query
      const q = searchQuery.toLowerCase().trim();
      if (q) {
        const matchName = (u.fullName || "").toLowerCase().includes(q);
        const matchEmail = (u.email || "").toLowerCase().includes(q);
        const matchId = String(u.id).includes(q) || `vr-${u.id}`.includes(q);
        if (!matchName && !matchEmail && !matchId) return false;
      }

      // Time Range
      const subDate = u.verificationSubmittedAt || u.updatedAt || u.createdAt;
      if (!isDateInRange(subDate, timeRange)) return false;

      return true;
    });

    // Sorting
    result.sort((a, b) => {
      const da = new Date(a.verificationSubmittedAt || a.updatedAt || a.createdAt || 0).getTime();
      const db = new Date(b.verificationSubmittedAt || b.updatedAt || b.createdAt || 0).getTime();
      return sortBy === "newest" ? db - da : da - db;
    });

    return result;
  }, [users, statusFilter, searchQuery, timeRange, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / 10));
  const safePage = Math.max(0, Math.min(page, totalPages - 1));

  const getVisiblePageNumbers = () => {
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }
    let start = safePage - Math.floor(maxVisible / 2);
    if (start < 0) {
      start = 0;
    } else if (start + maxVisible > totalPages) {
      start = totalPages - maxVisible;
    }
    return Array.from({ length: maxVisible }, (_, i) => start + i);
  };

  // Pagination calculation
  const paginatedUsers = useMemo(() => {
    const start = safePage * 10;
    return filteredUsers.slice(start, start + 10);
  }, [filteredUsers, safePage]);

  // Counts for metric cards and tab badges
  const counts = useMemo(() => {
    const total = users.length;
    const pending = users.filter((u) => !u.identityVerified && !u.verificationRejected).length;
    const verified = users.filter((u) => u.identityVerified).length;
    const rejected = users.filter((u) => u.verificationRejected).length;
    return { total, pending, verified, rejected };
  }, [users]);

  return (
    <>
      <div className="min-h-full bg-gray-50/50 p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Verification</h1>
            <p className="text-xs text-gray-500 mt-1">
              Review identity verification requests to ensure authentic and safe connections.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <DateFilterDropdown value={timeRange} onChange={setTimeRange} />
            <button
              onClick={fetchUsers}
              disabled={loading}
              className="px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-all flex items-center gap-2 shadow-2xs cursor-pointer"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-gray-500 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Metric Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Requests */}
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-xs font-semibold text-gray-400">Total Requests</span>
              <p className="text-2xl font-bold text-gray-900">{counts.total}</p>
              <span className="text-[11px] font-semibold text-emerald-600 flex items-center gap-1">
                ↑ 12.7% <span className="text-gray-400 font-normal">vs last 7 days</span>
              </span>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center text-purple-600">
              <ShieldCheck className="w-6 h-6" />
            </div>
          </div>

          {/* Pending Review */}
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-xs font-semibold text-gray-400">Pending Review</span>
              <p className="text-2xl font-bold text-gray-900">{counts.pending}</p>
              <span className="text-[11px] font-semibold text-amber-600 flex items-center gap-1">
                ↑ 9.1% <span className="text-gray-400 font-normal">vs last 7 days</span>
              </span>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center text-amber-600">
              <Clock className="w-6 h-6" />
            </div>
          </div>

          {/* Verified */}
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-xs font-semibold text-gray-400">Verified</span>
              <p className="text-2xl font-bold text-gray-900">{counts.verified}</p>
              <span className="text-[11px] font-semibold text-emerald-600 flex items-center gap-1">
                ↑ 16.4% <span className="text-gray-400 font-normal">vs last 7 days</span>
              </span>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600">
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>

          {/* Rejected */}
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-xs font-semibold text-gray-400">Rejected</span>
              <p className="text-2xl font-bold text-gray-900">{counts.rejected}</p>
              <span className="text-[11px] font-semibold text-rose-600 flex items-center gap-1">
                ↑ 11.1% <span className="text-gray-400 font-normal">vs last 7 days</span>
              </span>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-rose-50 flex items-center justify-center text-rose-600">
              <ShieldOff className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Filter Controls Bar */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-xs space-y-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Search Input */}
            <div className="relative flex-1 w-full">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email or request ID..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50/50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-all"
              />
            </div>

            {/* Sort Dropdown */}
            <SortDropdown value={sortBy} onChange={setSortBy} />
          </div>

          {/* Status Tabs */}
          <div className="flex items-center gap-2 border-t border-gray-100 pt-3 overflow-x-auto">
            {[
              { id: "ALL", label: "All Requests", count: counts.total },
              { id: "PENDING", label: "Pending", count: counts.pending },
              { id: "VERIFIED", label: "Verified", count: counts.verified },
              { id: "REJECTED", label: "Rejected", count: counts.rejected },
            ].map((tab) => {
              const active = statusFilter === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setStatusFilter(tab.id);
                    setPage(0);
                  }}
                  className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${active
                    ? "bg-purple-50 text-purple-700 shadow-2xs"
                    : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                >
                  <span>{tab.label}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${active ? "bg-purple-200/60 text-purple-800" : "bg-gray-100 text-gray-600"
                    }`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Requests Table Container */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50 text-gray-400 uppercase text-[10px] font-bold tracking-wider">
                  <th className="py-3.5 px-5">User</th>
                  <th className="py-3.5 px-4">Request ID</th>
                  <th className="py-3.5 px-4">Verification Type</th>
                  <th className="py-3.5 px-4">Submitted On</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-5 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 font-medium text-gray-700">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gray-200" />
                          <div className="space-y-1.5">
                            <div className="h-3 w-28 bg-gray-200 rounded" />
                            <div className="h-2.5 w-36 bg-gray-200 rounded" />
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4"><div className="h-3 w-16 bg-gray-200 rounded" /></td>
                      <td className="py-4 px-4"><div className="h-5 w-24 bg-gray-200 rounded-full" /></td>
                      <td className="py-4 px-4"><div className="h-3 w-28 bg-gray-200 rounded" /></td>
                      <td className="py-4 px-4"><div className="h-5 w-16 bg-gray-200 rounded-full" /></td>
                      <td className="py-4 px-5 text-right"><div className="h-7 w-16 bg-gray-200 rounded-xl ml-auto" /></td>
                    </tr>
                  ))
                ) : paginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-16 text-center text-gray-400">
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <ShieldCheck className="w-10 h-10 text-gray-300" />
                        <p className="font-semibold text-gray-700 text-sm">No verification requests found</p>
                        <p className="text-xs text-gray-400">Try adjusting your search or filters</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((u) => {
                    const reqIdFormatted = `#VR-${String(u.id).padStart(4, "0")}`;
                    const isVerified = u.identityVerified;
                    const isRejected = u.verificationRejected;
                    const isPending = !isVerified && !isRejected;

                    const govId = fixPhotoUrl(u.governmentIdUrl);
                    const selfie = fixPhotoUrl(u.selfieUrl);

                    const typeLabel = govId && selfie
                      ? "Government ID & Selfie"
                      : govId
                        ? "Government ID"
                        : selfie
                          ? "Selfie Verification"
                          : "Identity Verification";

                    const userPhoto = fixPhotoUrl(u.photoUrl1);

                    return (
                      <tr key={u.id} className="hover:bg-purple-50/30 transition-colors">
                        {/* User Column */}
                        <td className="py-3.5 px-5">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full overflow-hidden bg-purple-100 border border-purple-200 flex-shrink-0">
                              <SafeImage
                                src={userPhoto}
                                alt={u.fullName || "User"}
                                className="w-full h-full object-cover"
                                fallback={
                                  <div className="w-full h-full flex items-center justify-center text-purple-600 font-bold text-xs">
                                    {u.fullName?.charAt(0) || "U"}
                                  </div>
                                }
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 leading-tight truncate">{u.fullName || "Anonymous"}</p>
                              <p className="text-[11px] text-gray-400 truncate">{u.email}</p>
                            </div>
                          </div>
                        </td>

                        {/* Request ID */}
                        <td className="py-3.5 px-4 font-mono font-semibold text-gray-900">{reqIdFormatted}</td>

                        {/* Verification Type Badge */}
                        <td className="py-3.5 px-4">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                            {typeLabel}
                          </span>
                        </td>

                        {/* Submitted On */}
                        <td className="py-3.5 px-4 text-gray-600">{formatFullDate(u.verificationSubmittedAt || u.updatedAt || u.createdAt)}</td>

                        {/* Status */}
                        <td className="py-3.5 px-4">
                          {isVerified ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              Verified
                            </span>
                          ) : isRejected ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                              Rejected
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                              Pending
                            </span>
                          )}
                        </td>

                        {/* Action Button */}
                        <td className="py-3.5 px-5 text-right">
                          <button
                            onClick={() => setSelectedUser(u)}
                            className={`px-3.5 py-1.5 rounded-xl border text-xs font-semibold transition-all shadow-2xs cursor-pointer ${isPending
                              ? "border-purple-300 text-purple-700 bg-purple-50/50 hover:bg-purple-100/60"
                              : "border-gray-200 text-gray-700 bg-white hover:bg-gray-50"
                              }`}
                          >
                            {isPending ? "Review" : "View"}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50/30 text-xs text-gray-500">
              <p>
                Showing <span className="font-semibold text-gray-700">{filteredUsers.length > 0 ? safePage * 10 + 1 : 0}</span> to{" "}
                <span className="font-semibold text-gray-700">
                  {Math.min((safePage + 1) * 10, filteredUsers.length)}
                </span>{" "}
                of <span className="font-semibold text-gray-700">{filteredUsers.length}</span> requests
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={safePage === 0}
                  className="rounded-xl border-gray-200"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {getVisiblePageNumbers().map((idx) => (
                  <button
                    key={idx}
                    onClick={() => setPage(idx)}
                    className={`w-8 h-8 rounded-xl text-xs font-semibold transition-all cursor-pointer ${safePage === idx
                      ? "bg-purple-600 text-white shadow-2xs"
                      : "text-gray-600 hover:bg-gray-100"
                      }`}
                  >
                    {idx + 1}
                  </button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={safePage >= totalPages - 1}
                  className="rounded-xl border-gray-200"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Side Slide-Over Drawer */}
      {selectedUser && (
        <VerificationDrawer
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onApprove={handleApprove}
          onReject={(u) => setRejectTarget(u)}
          onUnverify={handleUnverify}
          actionLoading={actionLoading}
          onBanUser={(u) => setBanTarget(u)}
        />
      )}

      {/* Reject Modal */}
      {rejectTarget && (
        <RejectModal
          user={rejectTarget}
          onConfirm={handleReject}
          onCancel={() => setRejectTarget(null)}
          loading={!!actionLoading}
        />
      )}

      {/* Ban Confirmation Modal */}
      {banTarget && (
        <BanModal
          user={banTarget}
          onConfirm={handleConfirmBan}
          onCancel={() => setBanTarget(null)}
          loading={!!actionLoading}
        />
      )}
    </>
  );
};

export default Verification;
